import { paintIconAction } from './82-icon-actions.mjs';
import {
  bobResult,
  bobUnlockedWorld,
  mergeBobRecord,
} from '../../../packages/domain/src/72-bob-mastery.mjs';
import { createBobAudio } from './84-bob-audio.mjs';
const key = 'myaudience:bob-mastery-v7';
const sessionNotice =
  'World unlocks last for this session unless you save. Optional device records are visible to anyone using this browser. No upload or leaderboard.';
/** Device-only optional records, with explicit shared-device disclosure and
 * deletion. Storage failure leaves play usable and never claims a saved result. */
export function createBobOptions(
  ctx,
  options,
  { onControls, onAssist, onUnlocks, sound: soundControl, recordsHost = options } = {},
) {
  const mode = ctx.e('div', 'bob-control-mode');
  mode.setAttribute('role', 'group');
  mode.setAttribute('aria-label', 'Control style');
  let selectedMode = 'separate';
  const modeButtons = [];
  for (const [value, icon, text, hint] of [
    ['separate', 'gamepad-2', 'Arcade', 'Steer + Bounce'],
    ['classic', 'hand', 'Touch', 'Tap + drag'],
  ]) {
    const option = ctx.e('button', `bob-control-choice bob-control-${value}`);
    option.type = 'button';
    option.value = value;
    paintIconAction(ctx, option, icon, `${text}: ${hint}`, text);
    option.setAttribute('aria-pressed', String(value === selectedMode));
    modeButtons.push(option);
    mode.append(option);
  }
  function toggle(name, icon, title, hint) {
    const label = ctx.e('label', 'bob-arcade-toggle');
    const input = ctx.e('input', name);
    input.type = 'checkbox';
    const art = ctx.e('span', 'bob-toggle-icon');
    if (ctx.icon) art.append(ctx.icon(icon));
    art.setAttribute('aria-hidden', 'true');
    const copy = ctx.e('span', 'bob-toggle-copy');
    copy.append(ctx.e('strong', '', title), ctx.e('small', '', hint));
    const indicator = ctx.e('span', 'bob-toggle-indicator');
    indicator.setAttribute('aria-hidden', 'true');
    label.append(input, art, copy, indicator);
    return { label, input };
  }
  const { label: assistLabel, input: assist } = toggle(
    'bob-assist',
    'sparkles',
    'Timing assist',
    'Help with bounce timing · no Mastery medal',
  );
  const sound = soundControl || ctx.e('button', 'game-action bob-sound', 'Sound off');
  paintIconAction(ctx, sound, 'volume-x', 'Turn sound on');
  sound.type = 'button';
  sound.setAttribute('aria-pressed', 'false');
  const volumeLabel = ctx.e('label', 'bob-option-row', 'Effects volume');
  const volume = ctx.e('input', 'bob-volume');
  volume.type = 'range';
  volume.min = '0';
  volume.max = '100';
  volume.value = '45';
  volume.setAttribute('aria-label', 'Effects volume');
  volumeLabel.append(volume);
  const { label: saveLabel, input: save } = toggle(
    'bob-save',
    'medal',
    'Keep my progress',
    'Save worlds, medals and bests on this shared device',
  );
  const notice = ctx.e('p', 'bob-record-notice', sessionNotice);
  const records = ctx.e('p', 'bob-records');
  const erase = ctx.e('button', 'game-action bob-erase', 'Clear device records');
  erase.type = 'button';
  const credits = ctx.e('a', '', 'Sound effects: Kenney · CC0');
  credits.href = '/credits.html';
  credits.target = '_blank';
  credits.rel = 'noopener';
  const modeLabel = ctx.e('section', 'bob-control-section');
  modeLabel.append(ctx.e('h4', '', 'Controls'));
  modeLabel.append(mode);
  const testSound = ctx.e('button', 'game-action bob-test-sound', 'Test sound');
  testSound.type = 'button';
  paintIconAction(ctx, testSound, 'volume-2', 'Test sound');
  paintIconAction(ctx, erase, 'trash-2', 'Clear device records', 'Clear device records');
  const soundStatus = ctx.e('p', 'bob-audio-status', 'Sound off. Test sound to hear a preview.');
  soundStatus.setAttribute('role', 'status');
  options.append(modeLabel, assistLabel, volumeLabel, testSound, soundStatus, credits);
  if (!soundControl) options.append(sound);
  recordsHost.append(records, saveLabel, notice, erase);
  let stored = {},
    enabled = false,
    storage = null,
    currentLevel = 1;
  try {
    storage = globalThis.localStorage;
    const raw = storage?.getItem(key);
    if (raw && raw.length < 20000) {
      const parsed = JSON.parse(raw);
      if (parsed?.version === 5 && parsed.records && typeof parsed.records === 'object') {
        for (let i = 1; i <= 20; i++)
          if (parsed.records[i]) stored[i] = mergeBobRecord(parsed.records[i]);
        save.checked = true;
        notice.textContent =
          'Saved worlds, medals and bests restored on this shared device. No upload or leaderboard.';
      }
    }
  } catch {
    notice.textContent = 'Device storage is unavailable. Worlds and medals stay in this session.';
  }
  const audio = createBobAudio({
    onStatus(text) {
      soundStatus.textContent = text;
      paintIconAction(
        ctx,
        sound,
        enabled ? 'volume-2' : 'volume-x',
        enabled ? 'Turn sound off' : 'Turn sound on',
      );
      if (text.startsWith('Sound unavailable')) {
        enabled = false;
        paintIconAction(ctx, sound, 'volume-x', 'Retry sound');
      }
      sound.setAttribute('aria-pressed', String(enabled));
    },
  });
  const listeners = [];
  function listen(node, type, fn) {
    node.addEventListener(type, fn);
    listeners.push([node, type, fn]);
  }
  function paint(level = currentLevel) {
    currentLevel = level;
    const record = stored[level];
    records.textContent = record
      ? `World ${level}: ${record.medals.join(' · ') || 'No medals'} · Best ${record.score.toLocaleString()}${record.cleanTime ? ` · Clean ${(record.cleanTime / 1000).toFixed(1)}s` : ''}`
      : 'Finish a world to earn your first medal.';
  }
  function write() {
    if (!save.checked) return;
    try {
      if (!storage) throw new Error('unavailable');
      storage.setItem(key, JSON.stringify({ version: 5, records: stored }));
      notice.textContent =
        'Worlds, medals and bests saved on this shared device only. No upload or leaderboard.';
    } catch {
      notice.textContent = 'Could not save device records. World unlocks remain in this session.';
      save.checked = false;
    }
  }
  for (const button of modeButtons)
    listen(button, 'click', () => {
      selectedMode = button.value;
      for (const choice of modeButtons)
        choice.setAttribute('aria-pressed', String(choice.value === selectedMode));
      onControls?.();
    });
  listen(assist, 'change', () => onAssist?.(assist.checked));
  listen(sound, 'click', () => {
    enabled = !enabled;
    sound.setAttribute('aria-pressed', String(enabled));
    if (enabled) void audio.test();
    else void audio.enable(false);
  });
  listen(testSound, 'click', () => {
    enabled = true;
    void audio.test();
  });
  listen(volume, 'input', () => audio.setVolume(Number(volume.value) / 100));
  listen(save, 'change', () => {
    if (save.checked) write();
    else {
      try {
        storage?.removeItem(key);
        notice.textContent = sessionNotice;
      } catch {
        notice.textContent =
          'Could not remove stored records. Clear site data in browser settings.';
      }
    }
  });
  listen(erase, 'click', () => {
    try {
      storage?.removeItem(key);
      stored = {};
      save.checked = false;
      notice.textContent = 'Device records cleared. Start the world tour again from World 1.';
      paint();
      onUnlocks?.(bobUnlockedWorld(stored));
    } catch {
      notice.textContent = 'Could not clear records. Clear site data in browser settings.';
    }
  });
  paint();
  return {
    audio,
    get mode() {
      return selectedMode;
    },
    get assist() {
      return Boolean(assist.checked);
    },
    get unlockedLevel() {
      return bobUnlockedWorld(stored);
    },
    paint,
    finish(state) {
      const result = bobResult(state);
      if (result.complete) {
        stored[state.level] = mergeBobRecord(stored[state.level], result);
        write();
        paint(state.level);
        onUnlocks?.(bobUnlockedWorld(stored));
      }
      return result;
    },
    resumeAudio() {
      if (enabled) void audio.enable(true);
    },
    dispose() {
      audio.dispose();
      for (const [node, type, fn] of listeners) node.removeEventListener(type, fn);
    },
  };
}
